import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

import type { usersField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/usersField_updateWorkItemAllFieldTypesActionRenderer.graphql';
import { parseSuggestedAccountIds } from './parseSuggestedAccountIds';
import { useHydratedUsers } from './useHydratedUsers';
import { UsersFieldSkeleton } from './UsersFieldSkeleton';

const messages = defineMessages({
	assign: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.users-field.assign',
		defaultMessage: 'Assign {userNames} to {fieldName}',
		description: 'Label for the change action in the users field',
	},
	unassign: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.users-field.unassign',
		defaultMessage: 'Unassign users from {fieldName}',
		description: 'Label for the change action in the users field',
	},
});

export type Props = {
	queryRef: usersField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
	onInvalidAction?: () => void;
};

export const UsersField = ({
	queryRef,
	action,
	registerTooltipProvider,
	onInvalidAction,
}: Props): React.JSX.Element | null => {
	di(useHydratedUsers);

	const { formatMessage } = useIntl();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment usersField_updateWorkItemAllFieldTypesActionRenderer on JiraIssueField {
				... on JiraPeopleField {
					fieldId
					name
					selectedUsersConnection(first: 10) {
						edges {
							node {
								accountId
								name
							}
						}
					}
				}
				... on JiraMultipleSelectUserPickerField {
					fieldId
					name
					selectedUsersConnection(first: 10) {
						edges {
							node {
								accountId
								name
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	const suggestedUserAccountIds = useMemo(() => parseSuggestedAccountIds(fieldValue), [fieldValue]);

	// Determine if we need to hydrate: if any suggested id is not present in current field data
	const currentSelectedIds = (data?.selectedUsersConnection?.edges ?? [])
		.map((edge) => edge?.node?.accountId)
		.filter((accountId): accountId is string => typeof accountId === 'string');

	const isFieldRenderable = data?.fieldId === fieldKey;

	const needsHydration = Boolean(
		isFieldRenderable &&
		suggestedUserAccountIds.some((accountId) => !currentSelectedIds.includes(accountId)),
	);

	const hydratedData = useHydratedUsers(suggestedUserAccountIds, needsHydration, onInvalidAction);

	const { users, isLoading, error } =
		needsHydration && hydratedData
			? { users: hydratedData.users, isLoading: hydratedData.isLoading, error: hydratedData.error }
			: {
					users: (data?.selectedUsersConnection?.edges ?? [])
						.map((edge) => edge?.node)
						.filter(Boolean)
						.map((userAccount) => ({
							accountId: userAccount?.accountId,
							name: userAccount?.name ?? null,
						})),
					isLoading: false,
					error: null,
				};

	const isAssignable = (users?.length ?? 0) > 0 && suggestedUserAccountIds.length > 0;
	const isNotFound = (users?.length ?? 0) === 0 && !isLoading && suggestedUserAccountIds.length > 0;

	const userNames = (() => {
		const names = (users ?? [])
			.map((user) => user.name)
			.filter(Boolean)
			.sort();
		if (names.length <= 1) {
			return names.join('');
		}
		return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
	})();

	const assignMessage = formatMessage(messages.assign, {
		userNames,
		fieldName: data?.name,
	});

	const unassignMessage = formatMessage(messages.unassign, {
		fieldName: data?.name,
	});

	const formattedMessage = isAssignable ? assignMessage : unassignMessage;

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				suggested={userNames}
				fieldName={data?.name}
			/>
		),
		[fieldReasoning, userNames, data?.name],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable && !isNotFound && !error,
	});

	if (!isFieldRenderable || isNotFound || error) {
		return null;
	}

	if (isLoading) {
		return <UsersFieldSkeleton />;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.users-field"
		>
			{formattedMessage}
		</Text>
	);
};
